import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { OcrProvider, OcrJob } from '../types/api';

export function useOcrProviders() {
  return useQuery({
    queryKey: ['ocr-providers'],
    queryFn: async () => (await api.get<{ providers: OcrProvider[] }>('/ocr/providers')).providers,
  });
}

export function usePatchOcrProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; enabled?: boolean; notes?: string | null }) => {
      const { id, ...body } = input;
      return api.patch<{ provider: OcrProvider }>(`/ocr/providers/${id}`, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ocr-providers'] }),
  });
}

export function useOcrJobsForFile(fileId: string | null) {
  return useQuery({
    enabled: !!fileId,
    queryKey: ['ocr-jobs', fileId],
    queryFn: async () => (await api.get<{ jobs: OcrJob[] }>(`/ingest/files/${fileId}/ocr-jobs`)).jobs,
  });
}

export function useSubmitOcrJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { fileId: string; provider_slug: string }) =>
      api.post<{ job: OcrJob }>(`/ingest/files/${input.fileId}/ocr-jobs`, { provider_slug: input.provider_slug }),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['ocr-jobs', vars.fileId] }),
  });
}
